import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  IconButton, InputAdornment, Checkbox, FormControlLabel,
  Divider, Link, Alert, Menu, MenuItem, ListItemIcon, ListItemText,
  Chip,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Person, Business,
  KeyboardArrowDown, Handshake, BadgeOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getDefaultRoute } from '../../utils/permissions';

// Role options for end-users (admin is separate portal)
const USER_ROLES = [
  { role: 'free_user', label: 'Free User', icon: <Person />, color: '#00B894' },
  { role: 'business', label: 'Business', icon: <Business />, color: '#1565C0', isGroup: true },
];

const BUSINESS_SUB_ROLES = [
  { role: 'partner', label: 'Partner', icon: <Handshake sx={{ fontSize: 20 }} />, color: '#3182CE' },
  { role: 'individual_agent', label: 'Individual Agent', icon: <BadgeOutlined sx={{ fontSize: 20 }} />, color: '#805AD5' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginAsRole } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Selected role for demo quick-login
  const [selectedRole, setSelectedRole] = useState('free_user');
  const [selectedRoleLabel, setSelectedRoleLabel] = useState('Free User');

  // Menu anchors
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  const [businessMenuAnchor, setBusinessMenuAnchor] = useState(null);

  const handleLogin = async () => {
    setError('');
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    try {
      const success = await login(email, password, rememberMe);
      if (success) {
        if (success.role === 'admin') {
          setError('Admin users should use the Admin Portal login.');
          return;
        }
        navigate(getDefaultRoute(success.role));
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const user = await loginAsRole(selectedRole);
      if (user) {
        navigate(getDefaultRoute(selectedRole));
      }
    } catch (err) {
      setError('Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  // Role selector handlers
  const handleRoleMenuOpen = (e) => setRoleMenuAnchor(e.currentTarget);
  const handleRoleMenuClose = () => setRoleMenuAnchor(null);

  const selectRole = (role, label) => {
    setSelectedRole(role);
    setSelectedRoleLabel(label);
    handleRoleMenuClose();
  };

  const handleBusinessClick = (e) => {
    handleRoleMenuClose();
    setBusinessMenuAnchor(e.currentTarget || e.target);
  };

  const handleBusinessMenuClose = () => setBusinessMenuAnchor(null);

  const selectBusinessSubRole = (role, label) => {
    setSelectedRole(role);
    setSelectedRoleLabel(label);
    handleBusinessMenuClose();
  };

  const roleColor = selectedRole === 'free_user' ? '#00B894'
    : selectedRole === 'partner' ? '#3182CE' : '#805AD5';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 100%)',
        p: 2,
        position: 'relative',
      }}
    >
      {/* Top-right: Role selector chip */}
      <Box sx={{ position: 'absolute', top: 20, right: 24 }}>
        <Chip
          icon={selectedRole === 'free_user' ? <Person sx={{ fontSize: 18 }} /> : <Business sx={{ fontSize: 18 }} />}
          label={selectedRoleLabel}
          onClick={handleRoleMenuOpen}
          onDelete={handleRoleMenuOpen}
          deleteIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.82rem',
            border: '1px solid rgba(255,255,255,0.3)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
            '& .MuiChip-icon': { color: '#fff' },
            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
          }}
        />

        {/* Primary role menu: Free User | Business */}
        <Menu
          anchorEl={roleMenuAnchor}
          open={Boolean(roleMenuAnchor)}
          onClose={handleRoleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { mt: 0.5, borderRadius: 2, minWidth: 200 } }}
        >
          <MenuItem
            onClick={() => selectRole('free_user', 'Free User')}
            selected={selectedRole === 'free_user'}
          >
            <ListItemIcon><Person sx={{ color: '#00B894' }} /></ListItemIcon>
            <ListItemText>Free User</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleBusinessClick}>
            <ListItemIcon><Business sx={{ color: '#1565C0' }} /></ListItemIcon>
            <ListItemText>Business</ListItemText>
            <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary', ml: 1 }} />
          </MenuItem>
        </Menu>

        {/* Business sub-menu: Partner | Individual Agent */}
        <Menu
          anchorEl={businessMenuAnchor}
          open={Boolean(businessMenuAnchor)}
          onClose={handleBusinessMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { mt: 0.5, borderRadius: 2, minWidth: 220 } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#90A4AE', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Select Business Type
            </Typography>
          </Box>
          <Divider />
          {BUSINESS_SUB_ROLES.map(({ role, label, icon, color }) => (
            <MenuItem
              key={role}
              onClick={() => selectBusinessSubRole(role, label)}
              selected={selectedRole === role}
            >
              <ListItemIcon>{React.cloneElement(icon, { sx: { color } })}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        {/* Logo */}
        <Typography
          variant="h3"
          sx={{ textAlign: 'center', color: '#fff', fontWeight: 800, letterSpacing: 1, mb: 1 }}
        >
          iMoney
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.8)', mb: 3 }}
        >
          Welcome Back
        </Typography>

        {/* Login Card */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            <TextField
              fullWidth label="Email" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress} sx={{ mb: 2 }}
            />

            <TextField
              fullWidth label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} size="small" />
                }
                label={<Typography variant="body2">Remember me</Typography>}
              />
              <Link component="button" variant="body2" onClick={() => {}}
                sx={{ color: '#1B365D', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </Box>

            <Button
              fullWidth variant="contained" onClick={handleLogin} disabled={loading}
              sx={{
                py: 1.5, backgroundColor: '#1B365D', fontWeight: 600, fontSize: '1rem',
                textTransform: 'none', borderRadius: 2,
                '&:hover': { backgroundColor: '#142847' },
              }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>

            {/* Demo quick-login for selected role */}
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">or try a demo</Typography>
            </Divider>

            <Button
              fullWidth variant="outlined" onClick={handleDemoLogin} disabled={loading}
              sx={{
                py: 1.2, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                borderColor: roleColor, color: roleColor, borderRadius: 2,
                '&:hover': { borderColor: roleColor, backgroundColor: `${roleColor}10` },
              }}
            >
              Demo Login as {selectedRoleLabel}
            </Button>
          </CardContent>
        </Card>

        {/* Footer links */}
        <Box sx={{ textAlign: 'center', mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Don't have an account?{' '}
            <Link component="button" onClick={() => navigate('/register')}
              sx={{ color: '#00B894', fontWeight: 600, textDecoration: 'none' }}>
              Register
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <Link component="button" onClick={() => navigate('/admin/login')}
              sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500, textDecoration: 'none', fontSize: '0.75rem',
                '&:hover': { color: 'rgba(255,255,255,0.8)' },
              }}>
              Admin Portal Login →
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
